import React, { useState } from "react";
import { Form, Input, Select, Modal, Upload, Button, notification } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { languageOptions } from "../../utils";

const { Option } = Select;

const UploadVideoModal = ({
  visible,
  onCancel,
  onFinish,
  form,
  uploading,
  fileList,
  setFileList,
  selectedVideo,
  interpretersData,
  orgsData,
  title = "Upload Video",
  isEditMode = false,
}) => {
  const uploadProps = {
    accept: "video/*",
    beforeUpload: (file) => {
      const isVideo = file.type.startsWith("video/");
      if (!isVideo) {
        notification.error({
          message: "Invalid File",
          description: "Please upload video files only (MP4, WebM, etc.)",
        });
        return Upload.LIST_IGNORE;
      }
      const isLt2GB = file.size / 1024 / 1024 / 1024 < 2;
      if (!isLt2GB) {
        notification.error({
          message: "File Too Large",
          description: "Video must be smaller than 2GB",
        });
        return Upload.LIST_IGNORE;
      }
      setFileList([file]);
      return false;
    },
    fileList,
    onRemove: () => {
      setFileList([]);
    },
    listType: "picture",
    showUploadList: {
      showPreviewIcon: true,
      showDownloadIcon: false,
      showRemoveIcon: true,
    },
  };

  const handleModalClose = () => {
    form.resetFields();
    setFileList([]);
    onCancel();
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleModalClose}
      footer={null}
      width={600}
    >
      <Form form={form} layout="vertical" onFinish={onFinish} className="mt-4">
        <Form.Item
          name="title"
          label="Video Title"
          rules={[
            {
              required: true,
              message: "Please enter video title",
            },
          ]}
        >
          <Input placeholder="Type here..." className="rounded-xl" />
        </Form.Item>

        <Form.Item
          name="language"
          label="Sign Language"
          className="w-full"
          rules={[
            {
              required: true,
              message: "Please select a sign language",
            },
          ]}
        >
          <Select placeholder="Select language" className="rounded-xl h-11">
            {languageOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="description"
          label="Video Description"
          rules={[
            {
              required: true,
              message: "Please enter video description",
            },
          ]}
        >
          <Input.TextArea
            placeholder="Type here..."
            rows={4}
            className="rounded-xl"
          />
        </Form.Item>

        <div className="flex gap-2">
          <Form.Item
            name="interpreter"
            className="w-full"
            label="Presenter / Interpreter"
            rules={[
              {
                required: true,
                message: "Please select a presenter",
              },
            ]}
          >
            <Select placeholder="Select--" className="rounded-xl h-11">
              {interpretersData?.data?.map((interpreter) => (
                <Option key={interpreter._id} value={interpreter._id}>
                  {interpreter?.user?.full_name || "Unnamed Interpreter"}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="organization"
            className="w-full"
            label="Associated Organisation (Optional)"
          >
            <Select placeholder="Select--" className="rounded-xl h-11">
              {orgsData?.data?.map((org) => (
                <Option key={org._id} value={org._id}>
                  {org.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <Form.Item
          label={isEditMode ? "Update Video" : "Upload Video"}
          required
        >
          <Upload
            defaultFileList={
              selectedVideo?.url
                ? [
                    {
                      uid: "-1",
                      name: "current-video.mp4",
                      status: "done",
                      url: selectedVideo?.url,
                      type: "video/mp4",
                    },
                  ]
                : []
            }
            {...uploadProps}
            maxCount={1}
            disabled={isEditMode}
          >
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                isEditMode
                  ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                  : "border-gray-300 cursor-pointer hover:border-blue-500"
              }`}
            >
              <UploadOutlined
                className={`text-2xl mb-2 ${
                  isEditMode ? "text-gray-300" : "text-gray-400"
                }`}
              />
              <div
                className={`font-medium ${
                  isEditMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {isEditMode
                  ? "Video file (cannot be changed)"
                  : "Upload a video file"}
              </div>
              <div
                className={`text-sm mt-1 ${
                  isEditMode ? "text-gray-300" : "text-gray-400"
                }`}
              >
                {isEditMode
                  ? "To change the video, delete and re-upload"
                  : "MP4, WebM, MOV up to 2GB"}
              </div>
            </div>
          </Upload>
        </Form.Item>

        <Form.Item className="mb-0">
          <Button
            type="primary"
            htmlType="submit"
            loading={uploading}
            className="w-full h-12 rounded-xl bg-[#18181B]"
          >
            {isEditMode ? "Update Video" : "Upload Video"}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UploadVideoModal;
